import { useCallback, useMemo, useState } from "react";
import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import {
  MorphemeGlyphTileCharacter,
  type MorphemeGlyphTileCharacterFront,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui._components.character";
import type { GraphemeGlyph } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.entities";
import type {
  MorphemeGlyphTileGraphemeMorphemeSlice,
  MorphemeGlyphTileSlice,
  MorphemeGlyphTileWordMorphemeSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT,
  CARD_FRONT_BUTTON_WORD_SIDE_PADDING_MIN_CHARACTERS,
  getMorphemeGlyphTileGraphemeGlyphStyle,
  getMorphemeGlyphTilePronunciationStyle,
  getMorphemeGlyphTileWordGlyphStyle,
  getMorphemeGlyphTileWordPinyinStyle,
  getWordTilePinyinLines,
} from "#morpheme-glyph-tile-glyph-sizing";
import { cn } from "@-/com.npmjs.cn";
import arrayLength from "@-/com.npmjs.extra-array__length";

export type GlyphStyle = { fontSize: number; lineHeight: number };

/** Word-tile front: written glyphs (default) or the readings that make up the word. */
export type MorphemeGlyphTileWordFront = "glyphs" | "pinyin";

function wordFrontToCharacterFront(
  front: MorphemeGlyphTileWordFront,
): MorphemeGlyphTileCharacterFront {
  return front === "pinyin" ? "pinyin" : "glyph";
}

type MorphemeGlyphTileContentLayout = {
  height: number;
  width: number;
};

function MorphemeGlyphTileGraphemeContent({
  glyphStyle,
  graphemeGlyph,
  morphemeGlyphTileSlice,
  showPrimaryPinyin,
  uncolored,
}: {
  glyphStyle: GlyphStyle | null;
  graphemeGlyph?: GraphemeGlyph;
  morphemeGlyphTileSlice: MorphemeGlyphTileGraphemeMorphemeSlice;
  showPrimaryPinyin?: boolean;
  uncolored?: boolean;
}) {
  const { primaryPinyin } = morphemeGlyphTileSlice;
  const primaryPinyinStyle = useMemo(
    () =>
      showPrimaryPinyin === true && glyphStyle !== null && primaryPinyin
        ? getMorphemeGlyphTilePronunciationStyle({ glyphStyle, toneNumbersPinyin: primaryPinyin })
        : null,
    [glyphStyle, primaryPinyin, showPrimaryPinyin],
  );
  return (
    <MorphemeGlyphTileCharacter
      additionalPinyinList={morphemeGlyphTileSlice.additionalPinyin}
      glyphStyle={glyphStyle}
      grapheme={morphemeGlyphTileSlice.grapheme}
      graphemeGlyph={graphemeGlyph}
      pinyinDotsRowReserved
      primaryPinyin={primaryPinyin}
      primaryPinyinStyle={primaryPinyinStyle}
      uncolored={uncolored}
    />
  );
}

function MorphemeGlyphTileWordContent({
  front,
  glyphStyle,
  morphemeGlyphTileSlice,
  uncolored,
}: {
  front: MorphemeGlyphTileWordFront;
  glyphStyle: GlyphStyle | null;
  morphemeGlyphTileSlice: MorphemeGlyphTileWordMorphemeSlice;
  uncolored?: boolean;
}) {
  const characterCount = arrayLength(morphemeGlyphTileSlice.characters);
  const characterFront = wordFrontToCharacterFront(front);
  // Pinyin fronts already show the reading; skip the dots row (and its reserved band) so the
  // syllables can use the full tile.
  const reservePinyinDotsRow = front === "glyphs";

  // One-character words share the grapheme tile layout (centered in the full card with the dots-row
  // band reserved) so they match grapheme glyph tiles exactly.
  if (characterCount === 1) {
    const character = morphemeGlyphTileSlice.characters[0];
    if (character === undefined) {
      throw new Error("Single-character morpheme card is missing its character");
    }
    return (
      <MorphemeGlyphTileCharacter
        additionalPinyinList={[]}
        front={characterFront}
        glyphStyle={glyphStyle}
        grapheme={character.grapheme}
        pinyinDotsRowReserved={reservePinyinDotsRow}
        primaryPinyin={morphemeGlyphTileSlice.primaryPinyin[0] ?? null}
        uncolored={uncolored}
      />
    );
  }

  // Pinyin fronts stack each syllable on its own line (audio-first curriculum tiles), as a centered
  // block whose line pitch is the style's `lineHeight`; an erhua 儿 joins the line above it
  // (`diǎnr`). Glyph fronts keep the horizontal character row.
  if (front === "pinyin") {
    return (
      <View className="size-full min-w-0 flex-col items-center justify-center gap-0 overflow-hidden">
        {getWordTilePinyinLines(morphemeGlyphTileSlice.primaryPinyin).map(
          ({ characterIndex, erhua }) => {
            const grapheme = morphemeGlyphTileSlice.characters[characterIndex]?.grapheme;
            if (grapheme === undefined) {
              throw new Error(`Word tile pinyin line ${characterIndex} has no character`);
            }
            return (
              <MorphemeGlyphTileCharacter
                additionalPinyinList={[]}
                className="w-full"
                erhua={erhua}
                front={characterFront}
                glyphStyle={glyphStyle}
                grapheme={grapheme}
                key={`${grapheme}-${characterIndex}`}
                primaryPinyin={morphemeGlyphTileSlice.primaryPinyin[characterIndex] ?? null}
                uncolored={uncolored}
              />
            );
          },
        )}
      </View>
    );
  }

  return (
    <View
      className={cn(
        "size-full min-w-0 flex-row items-center justify-center gap-0 overflow-hidden",
        characterCount >= CARD_FRONT_BUTTON_WORD_SIDE_PADDING_MIN_CHARACTERS && "px-0",
      )}
    >
      {morphemeGlyphTileSlice.characters.map((character, index) => (
        <MorphemeGlyphTileCharacter
          additionalPinyinList={[]}
          className="h-full w-auto min-w-0 shrink"
          front={characterFront}
          glyphStyle={glyphStyle}
          grapheme={character.grapheme}
          key={`${character.grapheme}-${index}`}
          primaryPinyin={morphemeGlyphTileSlice.primaryPinyin[index] ?? null}
          uncolored={uncolored}
        />
      ))}
    </View>
  );
}

export function MorphemeGlyphTileContent({
  front = "glyphs",
  glyphStyle: glyphStyleOverride,
  graphemeGlyph,
  morphemeGlyphTileSlice,
  showPrimaryPinyin,
  uncolored,
}: {
  /**
   * Word tiles only. Curriculum word levels pass `"pinyin"` so the tile shows the readings that
   * make up the word (introduced by audio) instead of the written glyphs.
   */
  front?: MorphemeGlyphTileWordFront;
  /**
   * Pre-computed glyph size. When set, skips the per-tile `onLayout` measurement pass — use this
   * from virtualized grids (e.g. `LevelsList`) where the tile size is derivable from the column
   * count, so tiles render with the right `fontSize` on first commit instead of starting invisible
   * until `onLayout` fires. Without this, a missed `onLayout` (list recycling / `inset-0`
   * measurement race) leaves the glyph stuck at `opacity-0` even though borders + dots render.
   */
  glyphStyle?: GlyphStyle;
  graphemeGlyph?: GraphemeGlyph;
  morphemeGlyphTileSlice: MorphemeGlyphTileSlice;
  /** Grapheme pronunciation cards show the reading above their glyph. */
  showPrimaryPinyin?: boolean;
  uncolored?: boolean;
}) {
  const [contentLayout, setContentLayout] = useState<MorphemeGlyphTileContentLayout | null>(null);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;
    setContentLayout((previousLayout) => {
      if (
        previousLayout !== null &&
        previousLayout.width === width &&
        previousLayout.height === height
      ) {
        return previousLayout;
      }
      return { height, width };
    });
  }, []);

  const measuredGlyphStyle = useMemo((): GlyphStyle | null => {
    if (contentLayout === null) {
      return null;
    }

    // Single graphemes (and one-character words) are centered in the full card with the dots
    // overlaying the bottom whitespace, so reserve a dots-row of margin on BOTH sides — that keeps
    // the ink centered while leaving room for the dots below. Multi-character glyph words share
    // width across a row; pinyin fronts stack syllables and share height down the column.
    if (morphemeGlyphTileSlice.morphemeType === "word") {
      if (front === "pinyin") {
        return getMorphemeGlyphTileWordPinyinStyle({
          contentHeight: contentLayout.height,
          contentWidth: contentLayout.width,
          toneNumbersPinyinList: morphemeGlyphTileSlice.primaryPinyin,
        });
      }
      return getMorphemeGlyphTileWordGlyphStyle({
        characterCount: arrayLength(morphemeGlyphTileSlice.characters),
        contentHeight: contentLayout.height,
        contentWidth: contentLayout.width,
      });
    }

    return getMorphemeGlyphTileGraphemeGlyphStyle({
      glyphZoneHeight: Math.max(
        1,
        contentLayout.height - MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT * 2,
      ),
      glyphZoneWidth: Math.max(1, contentLayout.width),
    });
  }, [contentLayout, front, morphemeGlyphTileSlice]);

  const glyphStyle = glyphStyleOverride ?? measuredGlyphStyle;
  const skipMeasurement = glyphStyleOverride !== undefined;

  return (
    <View
      className="absolute inset-0 overflow-hidden"
      onLayout={skipMeasurement ? undefined : handleLayout}
    >
      {morphemeGlyphTileSlice.morphemeType === "grapheme" ? (
        <MorphemeGlyphTileGraphemeContent
          glyphStyle={glyphStyle}
          graphemeGlyph={graphemeGlyph}
          morphemeGlyphTileSlice={morphemeGlyphTileSlice}
          showPrimaryPinyin={showPrimaryPinyin}
          uncolored={uncolored}
        />
      ) : (
        <MorphemeGlyphTileWordContent
          front={front}
          glyphStyle={glyphStyle}
          morphemeGlyphTileSlice={morphemeGlyphTileSlice}
          uncolored={uncolored}
        />
      )}
    </View>
  );
}
